import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react'
import { useAuth } from './AuthContext'
import * as api from '../lib/api'

const TourContext = createContext(null)

// Helper to find visible element on screen (to support mobile/desktop selectors coexistence)
const findVisibleElement = (selector) => {
  if (!selector) return null
  const elements = document.querySelectorAll(selector)
  for (const el of elements) {
    const rect = el.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0 && window.getComputedStyle(el).display !== 'none') {
      return el
    }
  }
  return elements[0] || null
}

// ─── Шаги туров ────────────────────────────────────────────

// ────────────── OWNER GENERAL TOUR (10 шагов) ──────────────────────
const OWNER_STEPS = [
  {
    id: 'welcome',
    title: 'Добро пожаловать в PVZ Расчёт! 🚀',
    content: 'Давайте за 2 минуты настроим систему для вашего ПВЗ, добавим сотрудников и настроим правила расчета зарплаты.',
    icon: 'home',
    tab: null,
    target: null,
  },
  {
    id: 'pvz-select',
    title: 'Выбор активного ПВЗ 🏢',
    content: 'Здесь отображается текущий ПВЗ. Если у вас несколько пунктов выдачи, вы можете легко переключаться между ними или выбирать все сразу.',
    icon: 'home',
    tab: 'dashboard',
    target: '[data-tour="pvz-select"]',
  },
  {
    id: 'settings-menu',
    title: 'Переход в настройки ⚙️',
    content: 'Настройки — это сердце вашего личного кабинета. Перейдем сюда, чтобы настроить приглашения и формулу зарплаты.',
    icon: 'settings',
    tab: 'settings',
    tabParams: { tab: 'profile' },
    target: '[data-tour="settings-menu"]',
  },
  {
    id: 'invite-code-card',
    title: 'Приглашение сотрудников 👥',
    content: 'Создайте и скопируйте уникальную ссылку-инвайт. Отправьте её вашему сотруднику. Он перейдет по ней, зарегистрируется, и вы сможете подтвердить его в команде.',
    icon: 'users',
    tab: 'settings',
    tabParams: { tab: 'profile' },
    target: '[data-tour="invite-code-card"]',
  },
  {
    id: 'settings-salary-tab',
    title: 'Формула расчета зарплаты 💰',
    content: 'Перейдем во вкладку «Расчет ЗП», чтобы настроить ставки и бонусы для вашей команды.',
    icon: 'money',
    tab: 'settings',
    tabParams: { tab: 'salary' },
    target: '[data-tour="settings-salary-tab"]',
  },
  {
    id: 'salary-rates-card',
    title: 'Настройка ставок и премий 📊',
    content: 'Задайте фиксированную ставку за смену/час, укажите бонусы за операции приемки, выдачи и возвратов. Нажмите «Сохранить изменения» внизу, чтобы применить настройки.',
    icon: 'money',
    tab: 'settings',
    tabParams: { tab: 'salary' },
    target: '[data-tour="salary-rates-card"]',
  },
  {
    id: 'operations-menu',
    title: 'Рабочие смены и операции 📦',
    content: 'В этом разделе сотрудники начинают смену по PIN-коду и вносят количество операций. Система считает их зарплату в реальном времени.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-menu"]',
  },
  {
    id: 'payments-menu',
    title: 'Контроль зарплат и выплат 💵',
    content: 'Здесь отображается детализация заработка каждого сотрудника по сменам. Вы можете добавлять премии, штрафы, списывать удержания и отмечать выплаты.',
    icon: 'money',
    tab: 'payments',
    target: '[data-tour="payments-menu"]',
  },
  {
    id: 'analytics-menu',
    title: 'Финансовая аналитика P&L 📈',
    content: 'Вносите расходы на аренду, налоги и доходы от WB. Платформа автоматически построит P&L-отчет и покажет чистую прибыль вашего бизнеса.',
    icon: 'chart',
    tab: 'analytics',
    target: '[data-tour="analytics-menu"]',
  },
  {
    id: 'complete',
    title: 'Все готово к запуску! 🎉',
    content: 'Обучение завершено. Теперь вы можете выдать сервис сотрудникам. Поддержка и повторный запуск тура всегда доступны по кнопке ❓ в меню.',
    icon: 'check',
    tab: null,
    target: null,
  },
]

// ────────────── ADMIN GENERAL TOUR (5 шагов) ──────────────────────
const ADMIN_STEPS = [
  {
    id: 'welcome',
    title: 'Добро пожаловать, Администратор! 🛡️',
    content: 'Вы назначены администратором ПВЗ. Давайте быстро пройдем по основным функциям, которые помогут вам управлять пунктом.',
    icon: 'shield',
    tab: null,
    target: null,
  },
  {
    id: 'operations-menu',
    title: 'Контроль операций 📦',
    content: 'Здесь вы можете вносить операции за сотрудников, редактировать записи и следить за текущей сменой.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-menu"]',
  },
  {
    id: 'schedule-menu',
    title: 'Управление графиком смен 📅',
    content: 'Составляйте расписание работы. Кликните по любой ячейке в календаре, чтобы назначить сотрудника на смену.',
    icon: 'calendar',
    tab: 'schedule',
    target: '[data-tour="schedule-menu"]',
  },
  {
    id: 'payments-menu',
    title: 'Начисления и премии 💵',
    content: 'Проверяйте начисления сотрудников. В зависимости от прав, выданных владельцем, вы можете начислять премии и штрафы.',
    icon: 'money',
    tab: 'payments',
    target: '[data-tour="payments-menu"]',
  },
  {
    id: 'complete',
    title: 'Приступайте к работе! 🎉',
    content: 'Вы познакомились со всеми инструментами администратора. Если понадобится помощь, нажмите ❓ в боковом меню.',
    icon: 'check',
    tab: null,
    target: null,
  },
]

// ────────────── EMPLOYEE GENERAL TOUR (6 шагов) ──────────────────
const EMPLOYEE_STEPS = [
  {
    id: 'welcome',
    title: 'Добро пожаловать в команду! 👋',
    content: 'Этот тур поможет вам быстро разобраться, как отмечать смены, вносить операции и следить за своей зарплатой.',
    icon: 'home',
    tab: null,
    target: null,
  },
  {
    id: 'operations-menu',
    title: 'Ваша рабочая смена ⏱️',
    content: 'Перед началом работы обязательно нажмите «Начать смену» и введите ваш PIN-код. Таймер в реальном времени будет считать время и заработок.',
    icon: 'calendar',
    tab: 'operations',
    target: '[data-tour="operations-menu"]',
  },
  {
    id: 'operations-add',
    title: 'Внесение операций ＋',
    content: 'Вносите каждую операцию: Приёмка, Выдача, Возврат. Система мгновенно пересчитает вашу премию по тарифам владельца.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-add"]',
  },
  {
    id: 'payments-menu',
    title: 'Детализация зарплаты 💰',
    content: 'Здесь вы видите всю свою зарплату за месяц с разбивкой по дням. Если не согласны со штрафом, нажмите «Оспорить».',
    icon: 'money',
    tab: 'payments',
    target: '[data-tour="payments-menu"]',
  },
  {
    id: 'schedule-menu',
    title: 'Ваш график работы 📅',
    content: 'Смотрите свои запланированные смены на месяц вперед в удобном календаре.',
    icon: 'calendar',
    tab: 'schedule',
    target: '[data-tour="schedule-menu"]',
  },
  {
    id: 'complete',
    title: 'Все готово к первой смене! 🚀',
    content: 'Тур завершен. Вам начислен бонус 50 рублей за прохождение обучения! Успешной работы!',
    icon: 'check',
    tab: null,
    target: null,
  },
]

// ────────────── ДЕТАЛЬНЫЕ ШАГИ РАЗДЕЛОВ ────────────────────

// 1. ДАШБОРД (6 шагов)
const DASHBOARD_STEPS = [
  {
    id: 'dashboard-welcome',
    title: 'Обучение: Дашборд и KPI 📊',
    content: 'В этом туре мы разберем, как анализировать ключевые показатели вашего ПВЗ и следить за активными сменами.',
    icon: 'dashboard',
    tab: 'dashboard',
    target: null,
  },
  {
    id: 'dashboard-stats',
    title: 'Дашборд — KPI карточки 💳',
    content: 'Показатели выдачи, приемки, возвратов и удержаний за выбранный период. Кликните на карточку для детализации.',
    icon: 'dashboard',
    tab: 'dashboard',
    target: '[data-tour="dashboard-stats"]',
  },
  {
    id: 'dashboard-filters',
    title: 'Дашборд — фильтры периода 📅',
    content: 'Переключайте диапазон: Сегодня, Вчера, Неделя, Месяц. Можно выбрать конкретного сотрудника для анализа.',
    icon: 'calendar',
    tab: 'dashboard',
    target: '[data-tour="dashboard-filters"]',
  },
  {
    id: 'dashboard-chart',
    title: 'Дашборд — график операций 📈',
    content: 'Наглядный график операций и финансов по дням. Наведите на точку, чтобы увидеть детали конкретного дня.',
    icon: 'chart',
    tab: 'dashboard',
    target: '[data-tour="dashboard-chart"]',
  },
  {
    id: 'dashboard-complete',
    title: 'Дашборд изучен! 🎉',
    content: 'Отлично! Вы научились контролировать показатели ПВЗ. Вернуться к турам можно через меню ❓.',
    icon: 'check',
    tab: 'dashboard',
    target: null,
  },
]

// 2. РАБОЧАЯ СМЕНА И ОПЕРАЦИИ (9 шагов)
const OPERATIONS_STEPS = [
  {
    id: 'operations-welcome',
    title: 'Обучение: Рабочая смена и операции 📦',
    content: 'Давайте разберем, как сотрудники отмечают начало/конец смены и вносят данные по коробкам.',
    icon: 'package',
    tab: 'operations',
    target: null,
  },
  {
    id: 'operations-shift-timer',
    title: 'Таймер смены ⏱️',
    content: 'Здесь сотрудник начинает и завершает смену, вводя свой PIN-код. Таймер фиксирует время работы и начисления.',
    icon: 'calendar',
    tab: 'operations',
    target: '[data-tour="shift-timer"]',
  },
  {
    id: 'operations-add',
    title: 'Кнопка «＋ Добавить операцию» ＋',
    content: 'Основное действие для внесения коробок. Нажмите ➔ выберите тип (Приёмка, Выдача, Возврат) ➔ сохраните.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-add"]',
  },
  {
    id: 'operations-demo',
    title: 'Пример заполнения операции ✍️',
    content: 'Выберите тип, укажите количество штук/коробок и добавьте комментарий при необходимости.',
    example: '<strong>Тип:</strong> Выдача · <strong>Количество:</strong> 5 · <strong>Комментарий:</strong> «Выдан клиенту»',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-form"]',
  },
  {
    id: 'operations-tabs',
    title: 'Фильтрация операций 🔍',
    content: 'Вкладки Все / Приёмка / Выдача / Возвраты позволяют быстро отсортировать список внесенных записей.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-tabs"]',
  },
  {
    id: 'operations-filters',
    title: 'Поиск и пресеты дат 📅',
    content: 'Используйте календарь для выбора нужного периода или строку поиска для нахождения конкретной операции.',
    icon: 'calendar',
    tab: 'operations',
    target: '[data-tour="operations-filters"]',
  },
  {
    id: 'operations-view-switcher',
    title: 'Режимы просмотра: Лента и Шахматка 🔄',
    content: 'Вы можете переключаться между Лентой (списком операций) и Шахматкой — наглядным графиком выработки по часам.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-view-switcher"]',
  },
  {
    id: 'operations-chess-overview',
    title: 'Сетка «Шахматка» 📊',
    content: 'В этом режиме представлена подробная таблица посещаемости и выработки. Каждая строка — это сотрудник, а столбцы — дни месяца. Цветом выделен статус смены (фиолетовый — норма, красный — есть пропущенные или невыполненные операции).',
    icon: 'calendar',
    tab: 'operations',
    target: '[data-tour="operations-chess-grid"]',
  },
  {
    id: 'operations-chess-expand',
    title: 'Просмотр детальных операций 🔍',
    content: 'Нажмите на имя сотрудника или на стрелочку слева, чтобы раскрыть подробную раскладку по видам работ (Приёмка, Выдача, Возврат). Вы увидите точное число коробок за каждый день смены.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="operations-chess-grid"] .chess-employee-name-header',
  },
  {
    id: 'operations-chess-cell',
    title: 'Действия с ячейкой в Шахматке ⚡',
    content: 'Клик по ячейке смены открывает меню управления. Здесь вы можете быстро перейти к внесению данных («Внести данные»), либо разрешить сотруднику не выполнять данную операцию («Пропустить»), чтобы у него не висело предупреждение о неполной смене.',
    icon: 'package',
    tab: 'operations',
    target: '[data-tour="chess-cell-modal"]',
  },
  {
    id: 'operations-complete',
    title: 'Операции изучены! 🎉',
    content: 'Вы освоили работу с операциями и шахматкой. Внесенные данные напрямую влияют на расчет заработной платы.',
    icon: 'check',
    tab: 'operations',
    target: null,
  },
]

// 3. ЗАРПЛАТЫ И ВЫПЛАТЫ (7 шагов)
const PAYMENTS_STEPS = [
  {
    id: 'payments-welcome',
    title: 'Обучение: Зарплаты и выплаты 💰',
    content: 'Мы разберем, как контролировать начисления сотрудников, выплачивать им деньги и разрешать споры по штрафам.',
    icon: 'money',
    tab: 'payments',
    target: null,
  },
  {
    id: 'payments-header',
    title: 'Панель управления ⚙️',
    content: 'Кнопки для экспорта ведомости (Excel/CSV), очистки отмененных штрафов WB и ручного добавления премий/штрафов.',
    icon: 'settings',
    tab: 'payments',
    target: '[data-tour="payments-header"]',
  },
  {
    id: 'payments-table',
    title: 'Статистика фонда оплаты 💳',
    content: 'Сводные данные: общая сумма премий, удержаний WB, ФОТ к выплате, выданные суммы и остаток.',
    icon: 'money',
    tab: 'payments',
    target: '[data-tour="payments-table"]',
  },
  {
    id: 'payments-salary-table',
    title: 'Таблица начислений 📊',
    content: 'Показывает отработанные смены, ставки и бонусы каждого сотрудника. Кликните на строку для подневной детализации.',
    icon: 'money',
    tab: 'payments',
    target: '[data-tour="payments-salary-table"]',
  },
  {
    id: 'payments-payout',
    title: 'Проведение выплаты 💵',
    content: 'Нажмите кнопку «Выплатить» напротив имени сотрудника, чтобы зафиксировать выданный аванс или остаток зарплаты.',
    icon: 'money',
    tab: 'payments',
    target: '[data-tour="payments-salary-table"]',
  },
  {
    id: 'payments-dispute',
    title: 'Оспаривание штрафов ⚖️',
    content: 'Если сотрудник оспорил штраф, вы увидите оранжевый значок спора. Одобрите или отклоните его в детализации смен.',
    icon: 'money',
    tab: 'payments',
    target: '[data-tour="payments-salary-table"]',
  },
  {
    id: 'payments-complete',
    title: 'Зарплаты изучены! 🎉',
    content: 'Вы научились управлять выплатами и начислениями. Все расчеты прозрачны и автоматизированы.',
    icon: 'check',
    tab: 'payments',
    target: null,
  },
]

// 4. ГРАФИК СМЕН (6 шагов)
const SCHEDULE_STEPS = [
  {
    id: 'schedule-welcome',
    title: 'Обучение: График смен 📅',
    content: 'В этом туре мы научимся составлять расписание работы сотрудников на месяц вперед.',
    icon: 'calendar',
    tab: 'schedule',
    target: null,
  },
  {
    id: 'schedule-grid',
    title: 'Сетка графика 🗓️',
    content: 'Дни месяца по горизонтали, сотрудники по вертикали. Кликните по пустой ячейке, чтобы поставить сотрудника на смену.',
    icon: 'calendar',
    tab: 'schedule',
    target: '[data-tour="schedule-grid"]',
  },
  {
    id: 'schedule-nav',
    title: 'Навигация и фильтры 🔍',
    content: 'Переключайте месяцы стрелками и используйте фильтр по сотруднику, чтобы быстро просмотреть его график.',
    icon: 'calendar',
    tab: 'schedule',
    target: '[data-tour="schedule-nav"]',
  },
  {
    id: 'schedule-autoshift',
    title: 'Автоматический график ⚡',
    content: 'Используйте автосмены для генерации расписания по шаблонам (2/2, 5/2, через день) за пару кликов.',
    icon: 'calendar',
    tab: 'schedule',
    target: '[data-tour="schedule-autoshift"]',
  },
  {
    id: 'schedule-demo',
    title: 'Как назначить смену ✍️',
    content: 'Клик на пустую ячейку ➔ выбор сотрудника ➔ указание времени работы ➔ Сохранить.',
    example: '<strong>Сотрудник:</strong> Иван · <strong>Время:</strong> 09:00 – 21:00',
    icon: 'calendar',
    tab: 'schedule',
    target: '[data-tour="schedule-grid"]',
  },
  {
    id: 'schedule-complete',
    title: 'График изучен! 🎉',
    content: 'Расписание готово! Сотрудники увидят назначенные смены в своем приложении в реальном времени.',
    icon: 'check',
    tab: 'schedule',
    target: null,
  },
]

// 5. ФИНАНСОВАЯ АНАЛИТИКА P&L (6 шагов)
const ANALYTICS_STEPS = [
  {
    id: 'analytics-welcome',
    title: 'Обучение: Финансовая аналитика P&L 📈',
    content: 'Давайте разберем, как рассчитывать чистую прибыль вашего ПВЗ с учетом всех расходов.',
    icon: 'chart',
    tab: 'analytics',
    target: null,
  },
  {
    id: 'analytics-add',
    title: 'Внесение данных ＋',
    content: 'Используйте кнопку «＋ Добавить», чтобы внести выручку от WB (поступление) или расходы (аренда, интернет).',
    icon: 'chart',
    tab: 'analytics',
    target: '[data-tour="analytics-add"]',
  },
  {
    id: 'analytics-demo',
    title: 'Пример внесения расхода ✍️',
    content: 'Выберите категорию, укажите сумму и месяц, к которому относится данный расход.',
    example: '<strong>Категория:</strong> Аренда · <strong>Сумма:</strong> 25 000 ₽ · <strong>Период:</strong> Июнь 2026',
    icon: 'chart',
    tab: 'analytics',
    target: '[data-tour="analytics-add"]',
  },
  {
    id: 'analytics-chart',
    title: 'Графики и KPI 📊',
    content: 'Ключевые цифры: Выручка, Расходы, Чистая прибыль. График показывает динамику вашего баланса.',
    icon: 'chart',
    tab: 'analytics',
    target: '[data-tour="analytics-chart"]',
  },
  {
    id: 'analytics-pl',
    title: 'Таблица P&L (прибыли и убытки) 📝',
    content: 'Полный финансовый каскад. Разверните строки расходов и доходов, чтобы увидеть детализацию по статьям.',
    icon: 'chart',
    tab: 'analytics',
    target: '[data-tour="analytics-pl"]',
  },
  {
    id: 'analytics-complete',
    title: 'Аналитика изучена! 🎉',
    content: 'Отлично! Вносите расходы ежемесячно, чтобы видеть реальную доходность вашего бизнеса.',
    icon: 'check',
    tab: 'analytics',
    target: null,
  },
]

// 6. НАСТРОЙКИ ПЛАТФОРМЫ (6 шагов)
const SETTINGS_STEPS = [
  {
    id: 'settings-welcome',
    title: 'Обучение: Настройки платформы ⚙️',
    content: 'Давайте разберем, как управлять профилем, приглашать команду и настраивать формулу зарплаты.',
    icon: 'settings',
    tab: 'settings',
    target: null,
  },
  {
    id: 'settings-general',
    title: 'Вкладки настроек 🎛️',
    content: 'Здесь вы переключаетесь между профилем, внешним видом, командой, тарифами ЗП и другими опциями.',
    icon: 'settings',
    tab: 'settings',
    target: '[data-tour="settings-general"]',
  },
  {
    id: 'invite-code-card',
    title: 'Инвайты сотрудников 👥',
    content: 'В разделе Профиль вы можете создавать одноразовые ссылки-инвайты для сотрудников.',
    icon: 'users',
    tab: 'settings',
    tabParams: { tab: 'profile' },
    target: '[data-tour="invite-code-card"]',
  },
  {
    id: 'settings-salary-tab',
    title: 'Вкладка расчёта ЗП 💰',
    content: 'Перейдем на вкладку настроек ЗП, чтобы настроить оклады и бонусы.',
    icon: 'money',
    tab: 'settings',
    tabParams: { tab: 'salary' },
    target: '[data-tour="settings-salary-tab"]',
  },
  {
    id: 'salary-rates-card',
    title: 'Настройка формулы ЗП 📊',
    content: 'Задайте оклад за смену, бонусы за каждую коробку и штрафы. Нажмите Сохранить внизу аккордеона.',
    icon: 'money',
    tab: 'settings',
    tabParams: { tab: 'salary' },
    target: '[data-tour="salary-rates-card"]',
  },
  {
    id: 'settings-complete',
    title: 'Настройки изучены! 🎉',
    content: 'Вы освоили параметры PVZ Расчёт. Настройте один раз, и система будет считать ЗП автоматически.',
    icon: 'check',
    tab: 'settings',
    target: null,
  },
]

function getStepsForTour(role, tourType) {
  // Для общего тура при регистрации
  if (tourType === 'general' || !tourType) {
    if (role === 'owner') return OWNER_STEPS
    if (role === 'admin' || role === 'admin_employee') return ADMIN_STEPS
    return EMPLOYEE_STEPS
  }
  
  // Для конкретных мини-туров по разделам
  switch (tourType) {
    case 'dashboard':
      return DASHBOARD_STEPS
    case 'operations':
      return OPERATIONS_STEPS
    case 'payments':
      return PAYMENTS_STEPS
    case 'schedule':
      return SCHEDULE_STEPS
    case 'analytics':
      return ANALYTICS_STEPS
    case 'settings':
      return SETTINGS_STEPS
    default:
      return OWNER_STEPS
  }
}

export function TourProvider({ children, onNavigate }) {
  const { profile, userPvzs, setProfile } = useAuth()
  const [isActive, setIsActive] = useState(false)
  const [activeTourType, setActiveTourType] = useState('general')
  const [currentStep, setCurrentStep] = useState(0)
  const [targetRect, setTargetRect] = useState(null)
  const completionSavedRef = useRef(false)
  const retryTimersRef = useRef([])

  const rawSteps = profile ? getStepsForTour(profile.role, activeTourType) : []
  const steps = (userPvzs && userPvzs.length <= 1)
    ? rawSteps.filter(s => s.id !== 'pvz-select')
    : rawSteps

  // Текущий селектор target для TourGuide (для data-tour-active)
  const activeTargetSelector = isActive && steps[currentStep]?.target ? steps[currentStep].target : null

  // Очистка таймеров при размонтировании
  useEffect(() => {
    return () => {
      retryTimersRef.current.forEach(t => clearTimeout(t))
      retryTimersRef.current = []
    }
  }, [])

  // scrollIntoView + обновление rect
  const updateTargetRect = useCallback((selector) => {
    retryTimersRef.current.forEach(t => clearTimeout(t))
    retryTimersRef.current = []

    if (!selector) {
      setTargetRect(null)
      return
    }

    const findAndMeasure = () => {
      const el = findVisibleElement(selector)
      if (!el) {
        setTargetRect(null)
        return false
      }

      // Скролл к элементу
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })

      const measureRect = () => {
        const rect = el.getBoundingClientRect()
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        })
      }

      measureRect()
      const t = setTimeout(measureRect, 350)
      retryTimersRef.current.push(t)
      return true
    }

    if (!findAndMeasure()) {
      const delays = [200, 500, 900, 1500]
      delays.forEach(delay => {
        const t = setTimeout(() => findAndMeasure(), delay)
        retryTimersRef.current.push(t)
      })
    }
  }, [])

  // Обновляем rect при скролле/ресайзе
  useEffect(() => {
    if (!isActive) return
    const step = steps[currentStep]
    if (!step?.target) return

    const handleReposition = () => {
      const el = findVisibleElement(step.target)
      if (el) {
        const rect = el.getBoundingClientRect()
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        })
      }
    }
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)
    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isActive, currentStep, steps])

  // При переключении шага — навигация + scrollIntoView
  useEffect(() => {
    if (!isActive || !steps.length) return
    const step = steps[currentStep]
    if (step?.tab && onNavigate) {
      onNavigate(step.tab, step.tabParams)
    }
    const navDelay = step?.tab ? 300 : 0
    const t = setTimeout(() => {
      updateTargetRect(step?.target)
    }, navDelay)
    return () => clearTimeout(t)
  }, [isActive, currentStep])

  const startTour = useCallback((tourType = 'general') => {
    completionSavedRef.current = false
    setActiveTourType(tourType)
    setCurrentStep(0)
    setTargetRect(null)
    setIsActive(true)
  }, [])

  // Автозапуск тура при первой авторизации (всегда Быстрый старт)
  useEffect(() => {
    if (profile && !profile.is_tutorial_completed) {
      const autostartKey = `wb_tutorial_autostarted_${profile.id}`
      const completedKey = `wb_tour_completed_general_${profile.id}`
      
      const hasAutostarted = localStorage.getItem(autostartKey)
      const hasCompleted = localStorage.getItem(completedKey)
      
      if (!hasAutostarted && !hasCompleted) {
        localStorage.setItem(autostartKey, 'true')
        const t = setTimeout(() => {
          startTour('general')
        }, 1500)
        return () => clearTimeout(t)
      }
    }
  }, [profile, startTour])

  const nextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1)
    } else {
      endTour()
    }
  }, [currentStep, steps.length])

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1)
    }
  }, [currentStep])

  const goToStep = useCallback((idx) => {
    if (idx >= 0 && idx < steps.length) {
      setCurrentStep(idx)
    }
  }, [steps.length])

  const endTour = useCallback(async () => {
    setIsActive(false)
    setCurrentStep(0)
    setTargetRect(null)

    // Убираем data-tour-active со всех элементов
    document.querySelectorAll('[data-tour-active]').forEach(el => {
      el.removeAttribute('data-tour-active')
    })

    // Сохраняем прохождение
    if (!completionSavedRef.current && profile?.id) {
      completionSavedRef.current = true
      try {
        if (activeTourType) {
          localStorage.setItem(`wb_tour_completed_${activeTourType}_${profile.id}`, 'true')
        }
        const key = `wb_tutorial_${profile.id}`
        const existing = JSON.parse(localStorage.getItem(key) || '{"count": 0, "dates": []}')
        existing.count += 1
        existing.dates.push(new Date().toISOString())
        existing.lastCompleted = new Date().toISOString()
        localStorage.setItem(key, JSON.stringify(existing))

        const registry = JSON.parse(localStorage.getItem('wb_tutorial_registry') || '{}')
        registry[profile.id] = {
          name: profile.full_name,
          role: profile.role,
          count: existing.count,
          lastCompleted: existing.lastCompleted,
        }
        localStorage.setItem('wb_tutorial_registry', JSON.stringify(registry))

        // Бонус 50₽ за первое прохождение (не владельцу)
        if (!profile.is_tutorial_completed && existing.count === 1 && profile.role !== 'owner') {
          try {
            const today = new Date().toISOString().split('T')[0]
            await api.createPayment({
              employee_id: profile.id,
              type: 'bonus',
              amount: 50,
              comment: 'Бонус за прохождение обучения',
              period_start: today,
              period_end: today,
              created_by: profile.id,
            })
            await api.updateProfile(profile.id, { is_tutorial_completed: true })
            setProfile(prev => ({ ...prev, is_tutorial_completed: true }))
          } catch (e) {
            console.error('Tour bonus error:', e)
          }
        } else if (!profile.is_tutorial_completed) {
          // Для владельцев и админов просто помечаем как выполненное в БД (без начисления бонуса)
          try {
            await api.updateProfile(profile.id, { is_tutorial_completed: true })
            setProfile(prev => ({ ...prev, is_tutorial_completed: true }))
          } catch (e) {
            console.error('Tour complete status error:', e)
          }
        }
      } catch (e) {
        console.error('Tour save error:', e)
      }
    }
  }, [profile, activeTourType])

  return (
    <TourContext.Provider value={{
      isActive,
      currentStep,
      steps,
      targetRect,
      activeTargetSelector,
      startTour,
      nextStep,
      prevStep,
      goToStep,
      endTour,
      progress: steps.length ? ((currentStep + 1) / steps.length) * 100 : 0,
    }}>
      {children}
    </TourContext.Provider>
  )
}

export function useTour() {
  const ctx = useContext(TourContext)
  if (!ctx) throw new Error('useTour must be used within TourProvider')
  return ctx
}
